import {
  GRADE_ALLOCATABLE_FEATURE_KEYS,
  type GradeAllocatableFeatureKey,
} from '../../modules/platform/types/featureAccess';
import type {
  GradeAllocationMode,
  PutGradeFeatureAllocationsPayload,
} from '../../modules/platform/types/gradeFeatureAllocation';

/** Grades 1–12 as string keys (backend contract). */
export const GRADE_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'] as const;
export type GradeKey = (typeof GRADE_KEYS)[number];

export const ORDERED_ALLOCATABLE_KEYS: readonly GradeAllocatableFeatureKey[] = GRADE_ALLOCATABLE_FEATURE_KEYS;

export const ALLOCATABLE_FEATURE_LABELS: Record<GradeAllocatableFeatureKey, string> = {
  learning: 'Learning',
  ai_tutor: 'AI Tutor',
  communication_tutor: 'Communication Tutor',
  scenario_based_convo: 'Scenario-based Convo',
  grammar: 'Grammar',
  picture_talk: 'Picture Talk',
  story_studio: 'Story Studio',
};

/** Per-grade selected feature keys (draft UI state). */
export type MatrixState = Record<string, Set<string>>;

export function emptyMatrix(): MatrixState {
  const m: MatrixState = {};
  for (const g of GRADE_KEYS) m[g] = new Set<string>();
  return m;
}

export function cloneMatrix(matrix: MatrixState): MatrixState {
  const next = emptyMatrix();
  for (const g of GRADE_KEYS) next[g] = new Set(matrix[g] ?? []);
  return next;
}

const validFeature = new Set<string>(ORDERED_ALLOCATABLE_KEYS);

export function matrixFromByGrade(byGrade: Record<string, string[] | undefined> | null | undefined): MatrixState {
  const m = emptyMatrix();
  if (!byGrade || typeof byGrade !== 'object') return m;
  for (const g of GRADE_KEYS) {
    const arr = byGrade[g];
    if (!Array.isArray(arr)) continue;
    for (const f of arr) {
      if (validFeature.has(f)) m[g].add(f);
    }
  }
  return m;
}

/**
 * Build PUT body: only school-owned keys, stable column order, omit grades with no features.
 */
export function buildPayload(
  version: number,
  mode: GradeAllocationMode,
  matrix: MatrixState,
  schoolOwned: Record<string, boolean>
): PutGradeFeatureAllocationsPayload {
  const by_grade: Record<string, string[]> = {};
  for (const g of GRADE_KEYS) {
    const features = ORDERED_ALLOCATABLE_KEYS.filter((k) => matrix[g]?.has(k) && schoolOwned[k] === true);
    if (features.length > 0) by_grade[g] = [...features];
  }
  return { version, mode, by_grade };
}

export function stableFingerprint(
  version: number,
  mode: GradeAllocationMode,
  matrix: MatrixState,
  schoolOwned: Record<string, boolean>
): string {
  return JSON.stringify(buildPayload(version, mode, matrix, schoolOwned));
}
